import { Box, Collapse, IconButton, Stack, TableCell, TableRow, Typography } from "@mui/material"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import Iconify from "minimals-template/components/Iconify"
import { useState } from "react"
import { Classification } from "routes/schedule/tasks/classification"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { Bound } from "lib/@components/binding/Bound"
import { Time } from "routes/schedule/tasks/time"
import { Frequency } from "routes/schedule/tasks/frequency"
import { SkillSet } from "routes/schedule/tasks/skill-set"
import { TaskContent } from "./components/task-content"
import { createSlot } from "lib/@components/slot/create-slot"
import { grey } from "@mui/material/colors"

export const AuthoringTaskItemCell = createSlot("AuthoringTaskItemCell")
export const AuthoringTaskItemExtraCell = createSlot("AuthoringTaskItemExtraCell")

export function AuthoringTaskItem({ item, isOriginalTask = false, cellPlugsWidth = 150 }) {
    const [showMore, setShowMore] = useState(false)
    const { classification, title, minutes, isHeader, taskId } = item
    const { scheduleType, schedule } = useBoundContext()
    const { title: scheduleTitle, code: scheduleCode } = schedule
    const cellPlugs = AuthoringTaskItemCell.useSlot()
    const extraCellPlugs = AuthoringTaskItemExtraCell.useSlot()
    const [version] = useSearchParam("version", "no_v")
    const iconColumnWidth = 44
    if (isHeader) return null

    return (
        <Bound
            task={item}
            target={item}
            scheduleType={scheduleType}
            schedule={schedule}
            taskId={taskId}
            version={version}
        >
            <TableRow
                data-cy="authoring-task-item"
                onClick={() => setShowMore((show) => !show)}
                sx={{ cursor: "pointer" }}
                hover
            >
                <TableCell sx={{ maxWidth: { xs: 150, md: 300 }, whiteSpace: "noWrap", py: 0, pl: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Classification classification={classification} />
                        <TruncatedTypography
                            sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                flexGrow: 1,
                            }}
                        >
                            {title}
                        </TruncatedTypography>
                    </Stack>
                </TableCell>
                {isOriginalTask && (
                    <TableCell sx={{ maxWidth: { xs: 150, md: 300 }, py: 0 }}>
                        <TruncatedTypography noWrap>{scheduleTitle}</TruncatedTypography>
                        <TruncatedTypography noWrap variant="body2" color={grey[600]}>
                            #{scheduleCode}
                        </TruncatedTypography>
                    </TableCell>
                )}
                <Bound
                    timeLabelSx={{ textAlign: "center" }}
                    minHeightBoxSx={{ justifyContent: "center", marginRight: 1 }}
                >
                    <TableCell sx={{ width: 100, maxWidth: 100, whiteSpace: "noWrap", py: 0 }} align="center">
                        {minutes ? <Time /> : <Typography>-</Typography>}
                    </TableCell>
                    <TableCell sx={{ width: 100, maxWidth: 100, whiteSpace: "noWrap", py: 0 }} align="center">
                        <Frequency labelSx={{ textAlign: "center" }} />
                    </TableCell>
                </Bound>
                <TableCell sx={{ width: 80, maxWidth: 80, whiteSpace: "noWrap", py: 1 }} align="center">
                    <SkillSet sx={{ marginLeft: 0 }} />
                </TableCell>
                {cellPlugs?.length > 0 && (
                    <TableCell sx={{ width: cellPlugsWidth, maxWidth: cellPlugsWidth, whiteSpace: "noWrap", py: 0 }}>
                        <AuthoringTaskItemCell.Slot />
                    </TableCell>
                )}
                <TableCell
                    align="center"
                    padding="none"
                    sx={{
                        width: iconColumnWidth,
                        maxWidth: iconColumnWidth,
                    }}
                >
                    <IconButton
                        aria-label={showMore ? "Hide task details" : "Show task details"}
                        data-cy="show-task-details"
                    >
                        <Iconify icon={showMore ? "mingcute:up-fill" : "mingcute:down-fill"} />
                    </IconButton>
                </TableCell>
                {extraCellPlugs?.length > 0 && (
                    <TableCell sx={{ width: 60, maxWidth: 60, whiteSpace: "noWrap", py: 0 }} align="center">
                        <AuthoringTaskItemExtraCell.Slot />
                    </TableCell>
                )}
            </TableRow>
            <TableRow>
                <TableCell colSpan={6} sx={{ p: 0 }}>
                    <Box sx={{ overflow: "hidden", maxWidth: 1 }}>
                        <Collapse in={showMore} mountOnEnter sx={{ width: 1 }}>
                            <TaskContent />
                        </Collapse>
                    </Box>
                </TableCell>
            </TableRow>
        </Bound>
    )
}
